import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiMapPin, FiSearch, FiX } from 'react-icons/fi';

interface City {
  ms_city_id: number;
  ms_city_name: string;
}

interface JobSearchBarProps {
  onSearch: (searchTerm: string, locationId: number | null) => void;
  cities: City[];
  isLoading: boolean;
}

interface CityOption {
  id: number;
  label: string;
}

interface CityDropdownProps {
  options: CityOption[];
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
}

const CityDropdown: React.FC<CityDropdownProps> = ({ options, value, onChange, disabled }) => {
  const selectedLabel = options.find((option) => option.id === value)?.label ?? '';
  const [inputValue, setInputValue] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(options.find((option) => option.id === value)?.label ?? '');
  }, [value, options]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
        setInputValue(options.find((option) => option.id === value)?.label ?? '');
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value, options]);

  const filtered = inputValue.trim()
    ? options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
    : options;

  const handleSelect = (option: CityOption) => {
    onChange(option.id);
    setInputValue(option.label);
    setOpen(false);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
    setInputValue('');
    setOpen(false);
  };

  const handleToggleArrow = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    setOpen((current) => !current);
  };

  return (
    <div className="city-dd" ref={wrapRef}>
      <div className="city-dd__control" onClick={() => !disabled && setOpen(true)}>
        <span className="city-dd__icon" aria-hidden="true">
          <FiMapPin />
        </span>
        <input
          className="city-dd__input"
          placeholder="Semua Kota"
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            setOpen(true);
          }}
          disabled={disabled}
          onFocus={() => !disabled && setOpen(true)}
          autoComplete="off"
        />
        {value !== null && (
          <button className="city-dd__clear" onMouseDown={handleClear} title="Hapus pilihan" type="button">
            <FiX />
          </button>
        )}
        <button
          type="button"
          className={`city-dd__arrow${open ? ' city-dd__arrow--open' : ''}`}
          onMouseDown={handleToggleArrow}
          tabIndex={-1}
          aria-label={open ? 'Tutup daftar kota' : 'Buka daftar kota'}
        >
          <FiChevronDown />
        </button>
      </div>

      {open && (
        <ul className="city-dd__menu">
          {filtered.length === 0 ? (
            <li className="city-dd__empty">Tidak ditemukan</li>
          ) : (
            filtered.map((option) => (
              <li
                key={option.id}
                className={`city-dd__item${option.id === value ? ' city-dd__item--active' : ''}`}
                onMouseDown={() => handleSelect(option)}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const JobSearchBar: React.FC<JobSearchBarProps> = ({ onSearch, cities, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  const cityOptions: CityOption[] = cities.map((city) => ({
    id: city.ms_city_id,
    label: city.ms_city_name,
  }));

  const submitSearch = () => {
    onSearch(searchTerm.trim(), selectedLocationId);
  };

  return (
    <div className="jsb-wrap">
      <div className="jsb-inner">
        <div className="jsb-input-wrap">
          <FiSearch className="jsb-input-icon" />
          <input
            className="jsb-input"
            type="text"
            placeholder="Cari nama pekerjaan, skill, atau perusahaan"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submitSearch()}
            disabled={isLoading}
          />
        </div>

        <CityDropdown
          options={cityOptions}
          value={selectedLocationId}
          onChange={setSelectedLocationId}
          disabled={isLoading}
        />

        <button className="jsb-btn" onClick={submitSearch} disabled={isLoading}>
          <FiSearch />
          <span>{isLoading ? 'Memuat...' : 'Cari'}</span>
        </button>
      </div>
    </div>
  );
};

export default JobSearchBar;
